import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dumbbell,
  Plus,
  Edit2,
  Trash2,
  Search,
  Copy,
  Play,
  X,
  Check,
  Filter,
  Video,
  Target,
  Zap,
  Info,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2
} from 'lucide-react';

const CATEGORY_CONFIG = {
  chest: { name: 'Chest', color: 'text-red-400', bg: 'bg-red-500/10' },
  back: { name: 'Back', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  shoulders: { name: 'Shoulders', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  biceps: { name: 'Biceps', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  triceps: { name: 'Triceps', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  legs: { name: 'Legs', color: 'text-green-400', bg: 'bg-green-500/10' },
  core: { name: 'Core', color: 'text-teal-400', bg: 'bg-teal-500/10' },
  cardio: { name: 'Cardio', color: 'text-pink-400', bg: 'bg-pink-500/10' },
  full_body: { name: 'Full Body', color: 'text-indigo-400', bg: 'bg-indigo-500/10' }
};

const EQUIPMENT_CONFIG = {
  barbell: { name: 'Barbell', icon: '🏋️' },
  dumbbell: { name: 'Dumbbell', icon: '💪' },
  cable: { name: 'Cable', icon: '🔗' },
  machine: { name: 'Machine', icon: '⚙️' },
  bodyweight: { name: 'Bodyweight', icon: '🧘' },
  kettlebell: { name: 'Kettlebell', icon: '🔔' },
  resistance_band: { name: 'Resistance Band', icon: '〰️' },
  medicine_ball: { name: 'Medicine Ball', icon: '⚽' },
  trx: { name: 'TRX', icon: '🎗️' },
  none: { name: 'None', icon: '✋' }
};

const DIFFICULTY_CONFIG = {
  beginner: { label: 'Beginner', color: 'text-green-400', bg: 'bg-green-500/20' },
  intermediate: { label: 'Intermediate', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  advanced: { label: 'Advanced', color: 'text-red-400', bg: 'bg-red-500/20' }
};

export default function ExerciseLibraryPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [compoundFilter, setCompoundFilter] = useState('');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [viewingExercise, setViewingExercise] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'chest',
    equipment: 'barbell',
    difficulty: 'intermediate',
    video_url: '',
    instructions: '',
    muscles_targeted: [],
    is_compound: false,
    tips: []
  });
  
  const [muscleInput, setMuscleInput] = useState('');
  const [tipInput, setTipInput] = useState('');
  
  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchExercises = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('category', categoryFilter);
      if (equipmentFilter) params.append('equipment', equipmentFilter);
      if (difficultyFilter) params.append('difficulty', difficultyFilter);
      if (compoundFilter !== '') params.append('is_compound', compoundFilter);
      
      const res = await axios.get(`${API}/exercises/library?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setExercises(res.data.exercises || []);
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error('Failed to load exercises');
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm, categoryFilter, equipmentFilter, difficultyFilter, compoundFilter]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  const handleCreateExercise = () => {
    setFormData({
      name: '',
      category: 'chest',
      equipment: 'barbell',
      difficulty: 'intermediate',
      video_url: '',
      instructions: '',
      muscles_targeted: [],
      is_compound: false,
      tips: []
    });
    setEditingExercise(null);
    setShowCreateModal(true);
  };

  const handleEditExercise = (exercise) => {
    if (exercise.is_default) {
      toast.error('Cannot edit default exercises. Clone it first.');
      return;
    }
    setFormData({
      name: exercise.name,
      category: exercise.category,
      equipment: exercise.equipment,
      difficulty: exercise.difficulty,
      video_url: exercise.video_url || '',
      instructions: exercise.instructions || '',
      muscles_targeted: exercise.muscles_targeted || [],
      is_compound: exercise.is_compound || false,
      tips: exercise.tips || []
    });
    setEditingExercise(exercise);
    setShowCreateModal(true);
  };

  const handleSubmitExercise = async () => {
    if (!formData.name.trim()) {
      toast.error('Exercise name is required');
      return;
    }
    
    try {
      if (editingExercise) {
        await axios.put(`${API}/exercises/${editingExercise.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Exercise updated');
      } else {
        await axios.post(`${API}/exercises`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Exercise created');
      }
      setShowCreateModal(false);
      fetchExercises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save exercise');
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm('Delete this exercise?')) return;
    
    try {
      await axios.delete(`${API}/exercises/${exerciseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exercise deleted');
      fetchExercises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCloneExercise = async (exerciseId, exerciseName) => {
    try {
      await axios.post(`${API}/exercises/${exerciseId}/clone`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cloned "${exerciseName}"`);
      fetchExercises();
    } catch (error) {
      toast.error('Failed to clone exercise');
    }
  };

  const addArrayItem = (field, inputValue, setInputValue) => {
    if (inputValue.trim() && !formData[field].includes(inputValue.trim())) {
      setFormData(prev => ({ ...prev, [field]: [...prev[field], inputValue.trim()] }));
      setInputValue('');
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const ExerciseCard = ({ exercise }) => {
    const category = CATEGORY_CONFIG[exercise.category] || CATEGORY_CONFIG.chest;
    const difficulty = DIFFICULTY_CONFIG[exercise.difficulty] || DIFFICULTY_CONFIG.intermediate;
    const equipment = EQUIPMENT_CONFIG[exercise.equipment] || EQUIPMENT_CONFIG.bodyweight;
    
    return (
      <div 
        className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-all cursor-pointer"
        onClick={() => setViewingExercise(exercise)}
        data-testid={`exercise-card-${exercise.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className={`px-2 py-1 rounded text-xs font-medium ${category.bg} ${category.color}`}>
            {category.name}
          </div>
          <div className="flex items-center gap-2">
            {exercise.video_url && (
              <Video className="w-4 h-4 text-red-400" />
            )}
            {exercise.is_compound && (
              <Zap className="w-4 h-4 text-yellow-400" title="Compound movement" />
            )}
          </div>
        </div>
        
        <h3 className="text-white font-semibold mb-1">{exercise.name}</h3>
        
        <div className="flex items-center gap-2 mb-2 text-sm">
          <span className="text-gray-500">{equipment.icon} {equipment.name}</span>
          <span className="text-gray-600">•</span>
          <span className={`${difficulty.color}`}>{difficulty.label}</span>
        </div>
        
        {exercise.muscles_targeted?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {exercise.muscles_targeted.slice(0, 3).map((muscle, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-secondary/50 text-gray-400 rounded text-xs">
                {muscle}
              </span>
            ))}
            {exercise.muscles_targeted.length > 3 && (
              <span className="text-xs text-gray-500">+{exercise.muscles_targeted.length - 3}</span>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className={`text-xs ${exercise.is_default ? 'text-blue-400' : 'text-green-400'}`}>
            {exercise.is_default ? 'Default' : 'Custom'}
          </span>
          
          {isTrainer && (
            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => handleCloneExercise(exercise.id, exercise.name)}
                className="p-1.5 text-gray-400 hover:text-white transition-colors"
                title="Clone"
              >
                <Copy className="w-4 h-4" />
              </button>
              {!exercise.is_default && (
                <>
                  <button
                    onClick={() => handleEditExercise(exercise)}
                    className="p-1.5 text-gray-400 hover:text-white transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteExercise(exercise.id)}
                    className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exercise-library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Exercise Library</h1>
          <p className="text-gray-400">{exercises.length} exercises with video demonstrations</p>
        </div>
        
        {isTrainer && (
          <Button onClick={handleCreateExercise} data-testid="create-exercise-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Exercise
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search exercises or muscles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary"
              data-testid="search-exercises"
            />
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'border-primary' : ''}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-card border border-border rounded-lg">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-white text-sm"
              >
                <option value="">All Categories</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-gray-400 mb-1">Equipment</label>
              <select
                value={equipmentFilter}
                onChange={(e) => setEquipmentFilter(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-white text-sm"
              >
                <option value="">All Equipment</option>
                {Object.entries(EQUIPMENT_CONFIG).map(([key, { name }]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-gray-400 mb-1">Difficulty</label>
              <select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-white text-sm"
              >
                <option value="">All Levels</option>
                {Object.entries(DIFFICULTY_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select
                value={compoundFilter}
                onChange={(e) => setCompoundFilter(e.target.value)}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-white text-sm"
              >
                <option value="">All Types</option>
                <option value="true">Compound Only</option>
                <option value="false">Isolation Only</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Category Quick Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCategoryFilter('')}
          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
            !categoryFilter ? 'bg-primary text-white' : 'bg-secondary text-gray-400 hover:text-white'
          }`}
        >
          All
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, { name, color, bg }]) => (
          <button
            key={key}
            onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              categoryFilter === key ? `${bg} ${color}` : 'bg-secondary text-gray-400 hover:text-white'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      {/* Exercise Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {exercises.map(exercise => (
          <ExerciseCard key={exercise.id} exercise={exercise} />
        ))}
        
        {exercises.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Dumbbell className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No exercises found</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingExercise ? 'Edit Exercise' : 'Add Exercise'}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Exercise Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  placeholder="e.g., Bulgarian Split Squat"
                  data-testid="exercise-name-input"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  >
                    {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Equipment</label>
                  <select
                    value={formData.equipment}
                    onChange={(e) => setFormData(prev => ({ ...prev, equipment: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  >
                    {Object.entries(EQUIPMENT_CONFIG).map(([key, { name }]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Difficulty</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  >
                    {Object.entries(DIFFICULTY_CONFIG).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Video URL (YouTube embed)</label>
                <input
                  type="text"
                  value={formData.video_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  placeholder="https://www.youtube.com/embed/VIDEO_ID"
                />
                {formData.video_url && (
                  <div className="mt-2 aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src={formData.video_url}
                      className="w-full h-full"
                      allowFullScreen
                      title="Exercise video preview"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Instructions</label>
                <textarea
                  value={formData.instructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none"
                  placeholder="Step-by-step instructions for proper form..."
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Muscles Targeted</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.muscles_targeted.map((muscle, i) => (
                    <span key={i} className="px-2 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-1">
                      {muscle}
                      <button onClick={() => removeArrayItem('muscles_targeted', i)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={muscleInput}
                    onChange={(e) => setMuscleInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('muscles_targeted', muscleInput, setMuscleInput))}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    placeholder="e.g., Quadriceps"
                  />
                  <Button variant="outline" onClick={() => addArrayItem('muscles_targeted', muscleInput, setMuscleInput)}>Add</Button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Tips</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tips.map((tip, i) => (
                    <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                      {tip.length > 40 ? tip.slice(0, 40) + '...' : tip}
                      <button onClick={() => removeArrayItem('tips', i)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tipInput}
                    onChange={(e) => setTipInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('tips', tipInput, setTipInput))}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    placeholder="Form tip or variation..."
                  />
                  <Button variant="outline" onClick={() => addArrayItem('tips', tipInput, setTipInput)}>Add</Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <div className="text-white font-medium">Compound Movement</div>
                  <div className="text-sm text-gray-400">Works multiple muscle groups</div>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, is_compound: !prev.is_compound }))}
                  className={`w-12 h-6 rounded-full transition-colors ${formData.is_compound ? 'bg-primary' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${formData.is_compound ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmitExercise} className="flex-1" data-testid="save-exercise-btn">
                {editingExercise ? 'Update Exercise' : 'Add Exercise'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Exercise Modal */}
      {viewingExercise && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">{viewingExercise.name}</h2>
              <button onClick={() => setViewingExercise(null)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Video */}
              {viewingExercise.video_url ? (
                <div className="aspect-video rounded-lg overflow-hidden bg-black">
                  <iframe
                    src={viewingExercise.video_url}
                    className="w-full h-full"
                    allowFullScreen
                    title={viewingExercise.name}
                  />
                </div>
              ) : (
                <div className="aspect-video rounded-lg bg-secondary flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No video available</p>
                  </div>
                </div>
              )}
              
              {/* Meta info */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <div className={`text-lg font-medium ${CATEGORY_CONFIG[viewingExercise.category]?.color}`}>
                    {CATEGORY_CONFIG[viewingExercise.category]?.name}
                  </div>
                  <div className="text-xs text-gray-400">Category</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <div className="text-lg font-medium text-white">
                    {EQUIPMENT_CONFIG[viewingExercise.equipment]?.icon} {EQUIPMENT_CONFIG[viewingExercise.equipment]?.name}
                  </div>
                  <div className="text-xs text-gray-400">Equipment</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <div className={`text-lg font-medium ${DIFFICULTY_CONFIG[viewingExercise.difficulty]?.color}`}>
                    {DIFFICULTY_CONFIG[viewingExercise.difficulty]?.label}
                  </div>
                  <div className="text-xs text-gray-400">Difficulty</div>
                </div>
              </div>
              
              {/* Muscles */}
              {viewingExercise.muscles_targeted?.length > 0 && (
                <div>
                  <div className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Muscles Targeted
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {viewingExercise.muscles_targeted.map((muscle, i) => (
                      <span key={i} className="px-3 py-1.5 bg-primary/20 text-primary rounded-full text-sm">
                        {muscle}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Instructions */}
              {viewingExercise.instructions && (
                <div>
                  <div className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Instructions
                  </div>
                  <p className="text-gray-300 bg-secondary/50 rounded-lg p-3">
                    {viewingExercise.instructions}
                  </p>
                </div>
              )}
              
              {/* Tips */}
              {viewingExercise.tips?.length > 0 && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Tips</div>
                  <ul className="space-y-2">
                    {viewingExercise.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300">
                        <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Type badge */}
              <div className="flex items-center gap-4 pt-2 border-t border-border">
                {viewingExercise.is_compound && (
                  <span className="flex items-center gap-1 text-yellow-400 text-sm">
                    <Zap className="w-4 h-4" />
                    Compound Movement
                  </span>
                )}
                <span className={`text-sm ${viewingExercise.is_default ? 'text-blue-400' : 'text-green-400'}`}>
                  {viewingExercise.is_default ? 'Default Exercise' : 'Custom Exercise'}
                </span>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setViewingExercise(null)} className="flex-1">
                Close
              </Button>
              {isTrainer && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    handleCloneExercise(viewingExercise.id, viewingExercise.name);
                    setViewingExercise(null);
                  }}
                  className="flex-1"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Clone & Customize
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
